import { useQuery } from '@tanstack/react-query';
import { ctcApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';
import { CtcQueryOverrides } from 'src/api/queries/ctc/ctcSnapshotOptions';

/** @registry Purchase-order material lines for a CTC job, grouped by cost code. */
export function useCtcMaterials(
  jobNumber: string | null | undefined,
  overrides?: CtcQueryOverrides,
) {
  return useQuery({
    queryKey: queryKeys.ctc.materials(jobNumber ?? ''),
    queryFn: ({ signal }) =>
      ctcApiClient.getJobMaterialsByCostCode(jobNumber!, toCancelToken(signal)),
    enabled: !!jobNumber,
    staleTime: 0,
    ...overrides,
    meta: { errorContext: 'CTC materials' },
  });
}

/** @registry Committed/remaining material totals for a CTC job. */
export function useCtcMaterialsTotal(
  jobNumber: string | null | undefined,
  overrides?: CtcQueryOverrides,
) {
  return useQuery({
    queryKey: queryKeys.ctc.materialsTotal(jobNumber ?? ''),
    queryFn: ({ signal }) =>
      ctcApiClient.getJobMaterialsTotal(jobNumber!, toCancelToken(signal)),
    enabled: !!jobNumber,
    staleTime: 0,
    ...overrides,
    meta: { errorContext: 'CTC materials total' },
  });
}

/** @registry Material lines for a single cost code, loaded when a dialog opens. */
export function useCtcMaterialsForCode(
  jobNumber: string | null | undefined,
  costCode: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.ctc.materialsForCode(jobNumber ?? '', costCode ?? ''),
    queryFn: ({ signal }) =>
      ctcApiClient.getJobMaterialsWithCostCode(
        jobNumber!,
        costCode!,
        toCancelToken(signal),
      ),
    enabled: enabled && !!jobNumber && !!costCode,
    staleTime: 0,
    meta: { errorContext: 'CTC materials for cost code' },
  });
}
